import {
  alpha,
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Divider,
  Fab,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  CalendarMonth,
  Campaign,
  ChevronLeft,
  ChevronRight,
  Delete,
  Edit,
  Visibility,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "store";
import { AuthState } from "store/features/auth";
import { getNewsletters } from "apis";
import { useNavigate } from "react-router-dom";
import AddNewsletterDialog from "components/AddNewsletterDialog";
import EditNewsletterDialog from "components/EditNewsletterDialog";
import DeleteNewsletterDialog from "components/DeleteNewsletterDialog";

type Newsletter = {
  id: number;
  year: string;
  month: string;
  contentResume: string;
  contentMovement: string;
  contentBirthday: string;
  contentEvent: string;
  contentMessage: string;
  nurseryId: number;
  images: string[];
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_COLORS: Record<string, string> = {
  January: "#E3F2FD",
  February: "#FCE4EC",
  March: "#E8F5E9",
  April: "#FFF3E0",
  May: "#F3E5F5",
  June: "#E0F7FA",
  July: "#FFF8E1",
  August: "#EFEBE9",
  September: "#E8EAF6",
  October: "#FBE9E7",
  November: "#ECEFF1",
  December: "#E1F5FE",
};

const toMonthName = (month: string) => {
  const idx = parseInt(month, 10) - 1;
  return MONTH_NAMES[idx] || month;
};

const MONTH_ORDER: Record<string, number> = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};

const NewsletterPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const authInfo = useSelector<RootState, AuthState>((store) => store.auth);

  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const perPage = 6;

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Newsletter>();
  const [deleteId, setDeleteId] = useState<number>();

  const fetchData = useCallback(() => {
    setLoading(true);
    getNewsletters()
      .then((data) => setNewsletters(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDialogClose = useCallback(() => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    fetchData();
  }, [fetchData]);

  const sortedNewsletters = useMemo(() => {
    return newsletters.slice().sort((a, b) => {
      const yearDiff = dayjs(b.year).year() - dayjs(a.year).year();
      if (yearDiff !== 0) return yearDiff;
      return (MONTH_ORDER[b.month] || 0) - (MONTH_ORDER[a.month] || 0);
    });
  }, [newsletters]);

  const isAdmin = authInfo.role === "NURSERY_ADMIN";

  return (
    <>
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Container sx={{ py: 2 }}>
          {newsletters.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={8}
              color="text.secondary"
            >
              <Campaign
                sx={{ fontSize: 64, mb: 2, color: "action.disabled" }}
              />
              <Typography variant="h6" gutterBottom>
                {t("newsletter.empty")}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {t("newsletter.empty_hint")}
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {sortedNewsletters
                  .slice(page * perPage, page * perPage + perPage)
                  .map((newsletter) => {
                    const year = dayjs(newsletter.year).format("YYYY");
                    const monthName = toMonthName(newsletter.month);
                    const bgColor = MONTH_COLORS[monthName] || "#F5F5F5";
                    return (
                      <Grid item xs={12} sm={6} md={4} key={newsletter.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            transition: "box-shadow 0.2s, transform 0.2s",
                            "&:hover": {
                              boxShadow: 4,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          {/* Coloured header */}
                          <CardActionArea
                            onClick={() => {
                              navigate(`/newsletter/view?id=${newsletter.id}`, {
                                state: newsletter,
                              });
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: bgColor,
                                px: 2.5,
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <CalendarMonth
                                sx={{
                                  fontSize: 22,
                                  color: "text.secondary",
                                  opacity: 0.6,
                                }}
                              />
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={700}
                                  lineHeight={1.2}
                                >
                                  {t(`labels.months_name.${monthName}`)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={500}
                                >
                                  {year}
                                </Typography>
                              </Box>
                            </Box>

                            <CardContent sx={{ pb: 1.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.6,
                                  minHeight: 58,
                                }}
                              >
                                {newsletter.contentResume || "—"}
                              </Typography>
                            </CardContent>
                          </CardActionArea>

                          {/* Footer actions */}
                          <Divider />
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            px={1.5}
                            py={0.75}
                          >
                            <Tooltip title={t("newsletter.view")}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  navigate(
                                    `/newsletter/view?id=${newsletter.id}`,
                                    { state: newsletter },
                                  )
                                }
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isAdmin && (
                              <Box>
                                <Tooltip title={t("labels.edit")}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedItem(newsletter);
                                      setOpenEditDialog(true);
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("buttons.delete")}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setDeleteId(newsletter.id);
                                      setOpenDeleteDialog(true);
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>

              {/* Pagination */}
              {sortedNewsletters.length > perPage && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  mt={3}
                >
                  <IconButton
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage(page - 1)}
                    sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <Chip
                    label={`${page + 1} / ${Math.ceil(sortedNewsletters.length / perPage)}`}
                    size="small"
                    sx={{ fontWeight: 600, minWidth: 56 }}
                  />
                  <IconButton
                    size="small"
                    disabled={(page + 1) * perPage >= sortedNewsletters.length}
                    onClick={() => setPage(page + 1)}
                    sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Container>
      )}

      {/* Add FAB */}
      {isAdmin && (
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            pl: 2.5,
            pr: 3,
            gap: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 3,
            boxShadow: "0 4px 14px 0 rgba(0,0,0,0.25)",
            "&:hover": { boxShadow: "0 6px 20px 0 rgba(0,0,0,0.3)" },
          }}
          onClick={() => setOpenAddDialog(true)}
        >
          <Add />
          {t("newsletter.add")}
        </Fab>
      )}

      {/* Dialogs */}
      {openAddDialog && (
        <AddNewsletterDialog open={openAddDialog} onClose={handleDialogClose} />
      )}
      {openEditDialog && selectedItem && (
        <EditNewsletterDialog
          open={openEditDialog}
          onClose={handleDialogClose}
          item={selectedItem}
        />
      )}
      {openDeleteDialog && deleteId !== undefined && (
        <DeleteNewsletterDialog
          open={openDeleteDialog}
          onClose={handleDialogClose}
          id={deleteId}
        />
      )}
    </>
  );
};

export default NewsletterPage;
